import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ResultsPanel.module.css';
import CriterionCard   from './CriterionCard';
import TVChart         from './TVChart';
import PriceTicker     from './PriceTicker';
import KeyStats        from './KeyStats';
import RelatedHalal    from './RelatedHalal';
import ComplianceTrend      from './ComplianceTrend';
import StandardsComparison  from './StandardsComparison';
import ZakatCalculator      from './ZakatCalculator';

function fmt(n) {
  if (!n && n !== 0) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e12) return '$' + (n / 1e12).toFixed(2) + 'T';
  if (abs >= 1e9)  return '$' + (n / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6)  return '$' + (n / 1e6).toFixed(2) + 'M';
  return '$' + n.toLocaleString();
}

function fmtPeriod(dateStr) {
  if (!dateStr || dateStr === 'N/A') return null;
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch { return dateStr; }
}

function isStale(dateStr, months = 15) {
  if (!dateStr || dateStr === 'N/A') return false;
  try {
    return (Date.now() - new Date(dateStr).getTime()) > months * 30.44 * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

function fmtMoney(n) {
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}


export default function ResultsPanel({ data, theme, onScreen, isWatchlisted, onWatchlistToggle }) {
  const { t } = useTranslation();
  const { financials, analysis, historicalTrend, standards } = data;
  const a = analysis;
  const [tab,           setTab]           = useState('Overview');
  const [calcDividend,  setCalcDividend]  = useState('');
  const [calcGain,      setCalcGain]      = useState('');
  const [criteriaStd,   setCriteriaStd]   = useState('AAOIFI');

  const showZakat = a.overallVerdict === 'COMPLIANT' || a.overallVerdict === 'CONDITIONAL';

  const TAB_KEYS   = ['Overview', 'Shariah', 'Profile', 'Purification', ...(showZakat ? ['Zakat'] : [])];
  const TAB_LABELS = [
    t('results.tabs.overview'),
    t('results.tabs.shariah'),
    t('results.tabs.profile'),
    t('results.tabs.purification'),
    ...(showZakat ? ['Zakat'] : []),
  ];

  const calcPct        = (a.purificationPct || 0) / 100;
  const divAmt         = parseFloat(calcDividend) || 0;
  const gainAmt        = parseFloat(calcGain)     || 0;
  const donateDividend = divAmt  * calcPct;
  const donateGain     = gainAmt * calcPct;
  const donateTotal    = donateDividend + donateGain;
  const hasCalcValues  = divAmt > 0 || gainAmt > 0;

  return (
    <div className={styles.panel}>

      {/* ── Row 1: ticker (50%) | verdict + watchlist (50%) ── */}
      <div className={styles.headerRow}>
        <div className={styles.headerLeft}>
          <h2 className={styles.ticker}>{financials.ticker}</h2>
          <p className={styles.company}>{financials.companyName}</p>
          <p className={styles.meta}>
            {financials.exchange} · {financials.sector} · {financials.industry}
          </p>
          {fmtPeriod(financials.period) && (
            <div className={`${styles.filingBadge} ${isStale(financials.period) ? styles.filingBadgeStale : ''}`} style={{ marginTop: 8, display: 'inline-block' }}>
              {t('results.filing_badge')} · {fmtPeriod(financials.period)}
              {isStale(financials.period) && <span className={styles.filingStaleWarn}> · {t('results.filing_stale')}</span>}
            </div>
          )}
        </div>
        <div className={styles.headerRight}>
          {onWatchlistToggle && (
            <button
              className={`${styles.watchlistBtn} ${isWatchlisted ? styles.watchlistBtnActive : ''}`}
              onClick={() => onWatchlistToggle(financials.ticker, financials.companyName, a.overallVerdict, financials.price)}
              aria-label={isWatchlisted ? t('results.watchlist.remove_aria') : t('results.watchlist.save')}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill={isWatchlisted ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
              {isWatchlisted ? t('results.watchlist.saved') : t('results.watchlist.save')}
            </button>
          )}
          {standards && (() => {
            const verdicts = [standards.aaoifi.verdict, standards.djim.verdict, standards.msci.verdict];
            const c = verdicts.filter(v => v === 'COMPLIANT').length;
            const w = verdicts.filter(v => v === 'CONDITIONAL').length;
            const f = verdicts.filter(v => v === 'NON_COMPLIANT').length;
            const u = verdicts.filter(v => v === 'INSUFFICIENT_DATA').length;
            const parts = [
              c ? `${c} ${t('results.verdict.COMPLIANT')}` : null,
              w ? `${w} ${t('results.verdict.CONDITIONAL')}` : null,
              f ? `${f} ${t('results.verdict.NON_COMPLIANT')}` : null,
              u ? `${u} ${t('results.verdict.INSUFFICIENT_DATA')}` : null,
            ].filter(Boolean).join(' · ');

            const ratioSummary = (ratios) => {
              const pass = (ratios || []).filter(r => r.status === 'PASS').length;
              const warn = (ratios || []).filter(r => r.status === 'WARN').length;
              const fail = (ratios || []).filter(r => r.status === 'FAIL').length;
              return [
                pass ? `${pass} PASS` : null,
                warn ? `${warn} WARN` : null,
                fail ? `${fail} FAIL` : null,
              ].filter(Boolean).join(' · ');
            };

            return (
              <>
                <div className={styles.aggregateWrap}>
                  <div className={styles.agreeLine}>
                    {[standards.aaoifi.verdict, standards.djim.verdict, standards.msci.verdict].map((v, i) => (
                      <div key={i} className={styles.agreeSeg} style={{
                        flex: 1,
                        background: v === 'COMPLIANT' ? 'var(--green)' : v === 'CONDITIONAL' ? 'var(--amber)' : v === 'INSUFFICIENT_DATA' ? 'var(--border2)' : 'var(--red)'
                      }} />
                    ))}
                  </div>
                  <p className={styles.aggregateText}>{parts}</p>
                </div>
                <div className={styles.standardsGrid}>
                  {[
                    { name: 'AAOIFI', verdict: standards.aaoifi.verdict, ratios: standards.aaoifi.ratios },
                    { name: 'DJIM',   verdict: standards.djim.verdict,   ratios: standards.djim.ratios },
                    { name: 'MSCI',   verdict: standards.msci.verdict,   ratios: standards.msci.ratios },
                  ].map(({ name, verdict, ratios }) => (
                    <div key={name} className={styles.standardItem}>
                      <span className={styles.standardName}>{name}</span>
                      <div className={`${styles.standardBadge} ${
                        verdict === 'COMPLIANT'        ? styles.verdictCompliant :
                        verdict === 'CONDITIONAL'      ? styles.verdictConditional :
                        verdict === 'INSUFFICIENT_DATA'? styles.verdictInsufficient :
                                                         styles.verdictFail
                      }`}>
                        {t(`results.verdict.${verdict}`) || verdict.replace(/_/g, ' ')}
                      </div>
                      <span className={styles.ratioSummary}>{ratioSummary(ratios)}</span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* ── Row 2: price ticker (25%) | chart (75%) ── */}
      <div className={styles.priceChartRow}>
        <PriceTicker financials={financials} />
        <TVChart symbol={financials.ticker} theme={theme} />
      </div>

      {/* ── Tabs ── */}
      <div className={styles.tabs}>
        {TAB_KEYS.map((key, i) => (
          <button
            key={key}
            className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`}
            onClick={() => setTab(key)}
          >{TAB_LABELS[i]}</button>
        ))}
      </div>

      {/* ══ Overview ══ */}
      {tab === 'Overview' && (
        <>
          <div className={styles.metaStrip}>
            {[
              { label: t('results.overview.revenue'),      value: fmt(financials.revenue) },
              { label: t('results.overview.total_assets'), value: fmt(financials.totalAssets) },
              { label: t('results.overview.total_debt'),   value: fmt(financials.totalDebt) },
              { label: t('results.overview.market_cap'),   value: fmt(financials.marketCap) },
              { label: t('results.overview.period'),       value: financials.period },
            ].map(({ label, value }) => (
              <div key={label} className={styles.metaCell}>
                <div className={styles.metaLabel}>{label}</div>
                <div className={styles.metaValue}>{value}</div>
              </div>
            ))}
          </div>
          <KeyStats financials={financials} />
          <RelatedHalal
            currentTicker={financials.ticker}
            sector={financials.sector}
            onScreen={onScreen}
          />
        </>
      )}

      {/* ══ Shariah ══ */}
      {tab === 'Shariah' && (
        <>
          {/* ── Multi-Standard Comparison ── */}
          {standards && (
            <>
              <p className={styles.criteriaLabel} style={{ marginBottom: 13 }}>{t('results.shariah.multi_standard')}</p>
              <StandardsComparison standards={standards} />
            </>
          )}

          {/* ── Screening Criteria (switchable by standard) ── */}
          <div style={{ marginTop: 32, marginBottom: 8 }}>
            <div className={styles.criteriaHeader}>
              <p className={styles.criteriaLabel}>{t('results.shariah.screening_criteria')}</p>
              <div className={styles.stdSwitcher}>
                {['AAOIFI', 'DJIM', 'MSCI'].map(s => (
                  <button
                    key={s}
                    className={`${styles.stdBtn} ${criteriaStd === s ? styles.stdBtnActive : ''}`}
                    onClick={() => setCriteriaStd(s)}
                  >{s}</button>
                ))}
              </div>
            </div>

            {criteriaStd === 'AAOIFI' && (
              <div>
                {(a.criteria || []).map((c, i) => (
                  <CriterionCard key={c.id} criterion={c} index={i} />
                ))}
              </div>
            )}

            {(criteriaStd === 'DJIM' || criteriaStd === 'MSCI') && (() => {
              const std = standards?.[criteriaStd.toLowerCase()];
              if (!std) return null;
              const bizCriterion = a.criteria?.[0];
              const cards = [
                bizCriterion,
                ...(std.ratios || []).filter(ratio => ratio.formula !== null).map((ratio, i) => ({
                  id: i + 2,
                  name: ratio.name,
                  rule: ratio.formula,
                  status: ratio.status,
                  displayValue: ratio.value !== null ? ratio.value.toFixed(2) + '%' : 'N/A',
                  threshold: ratio.threshold,
                  barPct: ratio.value !== null && ratio.thresholdValue
                    ? Math.min((ratio.value / ratio.thresholdValue) * 100, 110) : null,
                  details: [
                    { label: 'Numerator',   value: fmt(ratio.numerator),   color: '' },
                    { label: 'Denominator', value: fmt(ratio.denominator), color: '' },
                    { label: 'Ratio',       value: ratio.value !== null ? ratio.value.toFixed(2) + '%' : 'N/A',
                      color: ratio.status === 'PASS' ? 'g' : ratio.status === 'FAIL' ? 'r' : 'a' },
                    { label: 'Threshold',   value: ratio.threshold,        color: '' },
                  ],
                  note: ratio.isApproximation ? ratio.approximationNote : '',
                  citation: null,
                })),
              ].filter(Boolean);
              return (
                <div>
                  {cards.map((c, i) => (
                    <CriterionCard key={i} criterion={c} index={i} />
                  ))}
                </div>
              );
            })()}
          </div>

          <ComplianceTrend trend={historicalTrend} />

          {/* Summary */}
          {/* <div className={styles.summary}>
            <h3 className={styles.summaryTitle}>Screening Summary (AAOIFI)</h3>
            <div className={styles.scoreBar}>
              {(a.criteria || []).map(c => (
                <div
                  key={c.id}
                  className={styles.scoreSeg}
                  style={{ background: STATUS_COLOR[c.status] || 'var(--border)' }}
                />
              ))}
            </div>
            <table className={styles.sumTable}>
              <thead>
                <tr>
                  <th>Criterion</th><th>Value</th><th>Threshold</th><th>Result</th>
                </tr>
              </thead>
              <tbody>
                {(a.summaryRows || []).map((row, i) => (
                  <tr key={i}>
                    <td>{row.criterion}</td>
                    <td style={{ color: VAL_COLOR[row.color] || 'var(--text-dim)' }}>{row.value}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{row.threshold}</td>
                    <td style={{ color: STATUS_COLOR[row.status] || 'var(--text)', textAlign: 'right' }}>
                      {t(`results.status.${row.status}`) || row.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {a.requiresPurification && a.purificationPct != null && (
              <div className={styles.purifyBanner} style={{ marginTop: 18, marginBottom: 0 }}>
                <div className={styles.purifyBannerTop}>
                  <div className={styles.purifyBannerLeft}>
                    <p className={styles.purifyBannerTitle}>⟁ Purification Required</p>
                    <p className={styles.purifyBannerText}>
                      This stock is permissible but earns incidental interest income.
                      Donate <strong>{a.purificationPct}%</strong> of dividend income
                      and <strong>{a.purificationPct}%</strong> of any capital gains
                      (profit only, not sale proceeds) to charity — tatheer.
                    </p>
                  </div>
                  <div className={styles.purifyBannerRight}>
                    <span className={styles.purifyBannerPct}>{a.purificationPct}%</span>
                    <p className={styles.purifyBannerPctLabel}>to purify</p>
                    <button
                      className={styles.calcToggle}
                      onClick={() => setCalcOpen(o => !o)}
                      aria-expanded={calcOpen}
                      aria-label={calcOpen ? 'Hide calculator' : 'Show calculator'}
                    >
                      <span className={`${styles.calcToggleChevron} ${calcOpen ? styles.calcToggleChevronOpen : ''}`}>▼</span>
                      {calcOpen ? 'hide calc' : 'calculator'}
                    </button>
                    {a.requiresPurification && (
                      <button className={styles.purifyTabLink} onClick={() => setTab('Purification')}>
                        Open Purification tab →
                      </button>
                    )}
                  </div>
                </div>
                {calcOpen && (
                  <div className={styles.calcSection}>
                    <p className={styles.calcTitle}>Purification Calculator</p>
                    <div className={styles.calcInputRow}>
                      <div className={styles.calcField}>
                        <label className={styles.calcLabel}>Dividends received</label>
                        <div className={styles.calcInputWrap}>
                          <span className={styles.calcPrefix}>$</span>
                          <input
                            className={styles.calcInput}
                            type="number" min="0" step="0.01" placeholder="0.00"
                            value={calcDividend}
                            onChange={e => setCalcDividend(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className={styles.calcField}>
                        <label className={styles.calcLabel}>Capital gain (profit only)</label>
                        <div className={styles.calcInputWrap}>
                          <span className={styles.calcPrefix}>$</span>
                          <input
                            className={styles.calcInput}
                            type="number" min="0" step="0.01" placeholder="0.00"
                            value={calcGain}
                            onChange={e => setCalcGain(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    {hasCalcValues && (
                      <div className={styles.calcResults}>
                        {divAmt > 0 && (
                          <div className={styles.calcRow}>
                            <span>Purify from dividends <span className={styles.calcRowPct}>({a.purificationPct}%)</span></span>
                            <span className={styles.calcRowVal}>{fmtMoney(donateDividend)}</span>
                          </div>
                        )}
                        {gainAmt > 0 && (
                          <div className={styles.calcRow}>
                            <span>Purify from capital gain <span className={styles.calcRowPct}>({a.purificationPct}%)</span></span>
                            <span className={styles.calcRowVal}>{fmtMoney(donateGain)}</span>
                          </div>
                        )}
                        <div className={styles.calcTotal}>
                          <span>Total to donate to charity</span>
                          <span className={styles.calcTotalVal}>{fmtMoney(donateTotal)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {a.nonCompliantFactors && (
              <div className={styles.failBox}>
                <p className={styles.failTitle}>✗ Non-Compliant Factors</p>
                <p>{a.nonCompliantFactors}</p>
              </div>
            )}
            {a.summaryNote && (
              <p className={styles.summaryNote}>
                <strong>Assessment:</strong> {a.summaryNote}
              </p>
            )}
          </div> */}
        </>
      )}

      {/* ══ Profile ══ */}
      {tab === 'Profile' && (
        <div className={styles.profileWrap}>
          {financials.description && (
            <p className={styles.description}>{financials.description}</p>
          )}
          <div className={styles.profileGrid}>
            {[
              { label: t('results.profile.sector'),        value: financials.sector },
              { label: t('results.profile.industry'),      value: financials.industry },
              { label: t('results.profile.country'),       value: financials.country },
              { label: t('results.profile.exchange'),      value: financials.exchange },
              { label: t('results.profile.employees'),     value: financials.employees?.toLocaleString() },
              { label: t('results.profile.filing_period'), value: financials.period },
            ].filter(r => r.value && r.value !== 'N/A').map(({ label, value }) => (
              <div key={label} className={styles.profileRow}>
                <span className={styles.profileLabel}>{label}</span>
                <span className={styles.profileValue}>{value}</span>
              </div>
            ))}
          </div>
          {financials.website && (
            <a
              href={financials.website}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.websiteLink}
            >
              {financials.website.replace(/^https?:\/\//, '')} ↗
            </a>
          )}
        </div>
      )}

      {/* ══ Purification ══ */}
      {tab === 'Purification' && !a.requiresPurification && (
        <div className={styles.purifyTabWrap}>
          <p className={styles.criteriaLabel} style={{ marginBottom: 16 }}>{t('results.purification.calculator_title')}</p>
          <div className={styles.purifyBanner} style={{ marginTop: 0, marginBottom: 0 }}>
            <div className={styles.purifyBannerTop}>
              <div className={styles.purifyBannerLeft}>
                <p className={styles.purifyBannerTitle}>{t('results.purification.no_purification_title')}</p>
                <p className={styles.purifyBannerText}>{t('results.purification.no_purification_text')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {tab === 'Purification' && a.requiresPurification && (
        <div className={styles.purifyTabWrap}>
          <p className={styles.criteriaLabel} style={{ marginBottom: 16 }}>{t('results.purification.calculator_title')}</p>
          <div className={styles.purifyBanner} style={{ marginTop: 0, marginBottom: 0 }}>
            <div className={styles.purifyBannerTop}>
              <div className={styles.purifyBannerLeft}>
                <p className={styles.purifyBannerTitle}>{t('results.purification.required_title')}</p>
                <p className={styles.purifyBannerText}>
                  {t('results.purification.required_text', { pct: a.purificationPct })}
                </p>
              </div>
              <div className={styles.purifyBannerRight}>
                <span className={styles.purifyBannerPct}>{a.purificationPct}%</span>
                <p className={styles.purifyBannerPctLabel}>{t('results.purification.to_purify')}</p>
              </div>
            </div>
            <div className={styles.calcSection}>
              <p className={styles.calcTitle}>{t('results.purification.calculator_title')}</p>
              <div className={styles.calcInputRow}>
                <div className={styles.calcField}>
                  <label className={styles.calcLabel}>{t('results.purification.dividends_label')}</label>
                  <div className={styles.calcInputWrap}>
                    <span className={styles.calcPrefix}>$</span>
                    <input className={styles.calcInput} type="number" min="0" step="0.01" placeholder="0.00"
                      value={calcDividend} onChange={e => setCalcDividend(e.target.value)} />
                  </div>
                </div>
                <div className={styles.calcField}>
                  <label className={styles.calcLabel}>{t('results.purification.capital_gain_label')}</label>
                  <div className={styles.calcInputWrap}>
                    <span className={styles.calcPrefix}>$</span>
                    <input className={styles.calcInput} type="number" min="0" step="0.01" placeholder="0.00"
                      value={calcGain} onChange={e => setCalcGain(e.target.value)} />
                  </div>
                </div>
              </div>
              {hasCalcValues && (
                <div className={styles.calcResults}>
                  {divAmt > 0 && (
                    <div className={styles.calcRow}>
                      <span>{t('results.purification.purify_dividends')} <span className={styles.calcRowPct}>({a.purificationPct}%)</span></span>
                      <span className={styles.calcRowVal}>{fmtMoney(donateDividend)}</span>
                    </div>
                  )}
                  {gainAmt > 0 && (
                    <div className={styles.calcRow}>
                      <span>{t('results.purification.purify_capital_gain')} <span className={styles.calcRowPct}>({a.purificationPct}%)</span></span>
                      <span className={styles.calcRowVal}>{fmtMoney(donateGain)}</span>
                    </div>
                  )}
                  <div className={styles.calcTotal}>
                    <span>{t('results.purification.total_to_donate')}</span>
                    <span className={styles.calcTotalVal}>{fmtMoney(donateTotal)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ Zakat ══ */}
      {tab === 'Zakat' && showZakat && (
        <ZakatCalculator financials={financials} />
      )}

    </div>
  );
}
